import React from "react";
import { api, formatApiError } from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { Plus, Trash2, TrendingUp } from "lucide-react";

const CHANNELS = ["meta", "google", "youtube", "amazon", "tiktok", "snapchat", "pinterest", "linkedin", "reddit", "x"];

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function AdminAdMetrics() {
    const [campaigns, setCampaigns] = React.useState([]);
    const [items, setItems] = React.useState([]);
    const [totals, setTotals] = React.useState({ spend: 0, revenue: 0, roas: 0 });
    const [filter, setFilter] = React.useState("all");
    const [form, setForm] = React.useState({
        campaign_id: "",
        channel: "meta",
        date: todayStr(),
        spend: "",
        revenue: "",
        note: "",
    });

    const load = React.useCallback(async () => {
        const params = filter !== "all" ? { campaign_id: filter } : {};
        const [c, m] = await Promise.all([
            api.get("/admin/campaigns"),
            api.get("/admin/ad-metrics", { params }),
        ]);
        setCampaigns(c.data);
        setItems(m.data.items);
        setTotals(m.data.totals);
    }, [filter]);

    React.useEffect(() => {
        load();
    }, [load]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.campaign_id) {
            toast.error("Pick a campaign");
            return;
        }
        try {
            await api.post("/admin/ad-metrics", {
                ...form,
                spend: parseFloat(form.spend) || 0,
                revenue: parseFloat(form.revenue) || 0,
            });
            toast.success("Logged");
            setForm({ ...form, spend: "", revenue: "", note: "" });
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this entry?")) return;
        await api.delete(`/admin/ad-metrics/${id}`);
        load();
    };

    return (
        <div className="space-y-6" data-testid="admin-ad-metrics">
            {/* Totals */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="card-ecom p-4">
                    <div className="text-xs text-slate-500">Total spend</div>
                    <div className="font-display text-2xl font-semibold mt-1">
                        ${totals.spend.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </div>
                </div>
                <div className="card-ecom p-4">
                    <div className="text-xs text-slate-500">Total revenue</div>
                    <div className="font-display text-2xl font-semibold mt-1 text-coral">
                        ${totals.revenue.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </div>
                </div>
                <div className="card-ecom p-4">
                    <div className="text-xs text-slate-500">Blended ROAS</div>
                    <div className="font-display text-2xl font-semibold mt-1 text-lime-ink">
                        {totals.roas}x
                    </div>
                </div>
                <div className="card-ecom p-4">
                    <div className="text-xs text-slate-500">Entries</div>
                    <div className="font-display text-2xl font-semibold mt-1">{items.length}</div>
                </div>
            </div>

            {/* Add form */}
            <form
                onSubmit={submit}
                className="card-ecom p-5 space-y-4"
                data-testid="ad-metric-form"
            >
                <div className="flex items-center gap-2 mb-1">
                    <Plus className="w-4 h-4 text-coral" />
                    <h3 className="font-display text-lg font-semibold">Log a day of ad data</h3>
                </div>
                <p className="text-sm text-slate-500 -mt-2">
                    Use this to manually enter Meta / Google / Amazon spend &amp; revenue per campaign per day, until OAuth approvals come through.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                    <div className="md:col-span-2 space-y-1.5">
                        <Label className="text-xs text-slate-500">Campaign</Label>
                        <Select
                            value={form.campaign_id}
                            onValueChange={(v) => setForm({ ...form, campaign_id: v })}
                        >
                            <SelectTrigger className="rounded-xl border-slate-200 h-10" data-testid="metric-campaign">
                                <SelectValue placeholder="Pick a campaign" />
                            </SelectTrigger>
                            <SelectContent>
                                {campaigns.map((c) => (
                                    <SelectItem key={c.id} value={c.id}>
                                        {c.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs text-slate-500">Channel</Label>
                        <Select
                            value={form.channel}
                            onValueChange={(v) => setForm({ ...form, channel: v })}
                        >
                            <SelectTrigger className="rounded-xl border-slate-200 h-10" data-testid="metric-channel">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {CHANNELS.map((c) => (
                                    <SelectItem key={c} value={c}>
                                        {c}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs text-slate-500">Date</Label>
                        <Input
                            type="date"
                            value={form.date}
                            onChange={(e) => setForm({ ...form, date: e.target.value })}
                            className="rounded-xl border-slate-200 h-10"
                            data-testid="metric-date"
                            required
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs text-slate-500">Spend ($)</Label>
                        <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={form.spend}
                            onChange={(e) => setForm({ ...form, spend: e.target.value })}
                            className="rounded-xl border-slate-200 h-10"
                            data-testid="metric-spend"
                            required
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs text-slate-500">Revenue ($)</Label>
                        <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={form.revenue}
                            onChange={(e) => setForm({ ...form, revenue: e.target.value })}
                            className="rounded-xl border-slate-200 h-10"
                            data-testid="metric-revenue"
                            required
                        />
                    </div>
                </div>
                <button
                    type="submit"
                    className="btn-pill-primary inline-flex items-center"
                    data-testid="metric-submit"
                >
                    <TrendingUp className="w-4 h-4 mr-2" /> Log entry
                </button>
            </form>

            {/* Filter */}
            <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-wider text-slate-500">Filter:</span>
                <Select value={filter} onValueChange={setFilter}>
                    <SelectTrigger className="rounded-full border-slate-200 h-9 w-64" data-testid="metric-filter">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All campaigns</SelectItem>
                        {campaigns.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                                {c.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Entries table */}
            <div className="card-ecom overflow-hidden" data-testid="metrics-list">
                {items.length === 0 ? (
                    <div className="p-10 text-center text-sm text-slate-500">
                        No entries yet. Log your first day of ad data above to get started.
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-[1.4fr_0.7fr_0.7fr_0.7fr_0.7fr_0.5fr_0.4fr] px-4 py-3 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                            <div>Campaign</div>
                            <div>Channel</div>
                            <div>Date</div>
                            <div className="text-right">Spend</div>
                            <div className="text-right">Revenue</div>
                            <div className="text-right">ROAS</div>
                            <div></div>
                        </div>
                        {items.map((it) => {
                            const roas = it.spend > 0 ? (it.revenue / it.spend).toFixed(2) : "—";
                            return (
                                <div
                                    key={it.id}
                                    className="grid grid-cols-[1.4fr_0.7fr_0.7fr_0.7fr_0.7fr_0.5fr_0.4fr] items-center px-4 py-3 text-sm border-t hairline"
                                    data-testid={`metric-${it.id}`}
                                >
                                    <div className="font-semibold text-slate-900 truncate">
                                        {it.campaign_name}
                                    </div>
                                    <div>
                                        <span className="chip chip-lime text-[10px]">{it.channel}</span>
                                    </div>
                                    <div className="text-slate-600 text-xs">{it.date}</div>
                                    <div className="text-right text-slate-700">${it.spend.toFixed(2)}</div>
                                    <div className="text-right font-semibold text-coral">
                                        ${it.revenue.toFixed(2)}
                                    </div>
                                    <div className="text-right text-lime-ink font-semibold">{roas}x</div>
                                    <div className="text-right">
                                        <button
                                            onClick={() => remove(it.id)}
                                            className="text-slate-400 hover:text-coral"
                                            data-testid={`del-metric-${it.id}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </>
                )}
            </div>
        </div>
    );
}
